import { graphql, Link as NavLink, useStaticQuery, withPrefix } from 'gatsby';
import {
  Menu,
  MenuItem,
  Utils,
  MenuGroup,
  Search,
  Dropdown,
} from '@hiro-ui/bootstrap';
import { useLocation } from '@reach/router';
import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Index } from 'elasticlunr';
import styled from 'styled-components';

import { GetNavItemsQuery } from '../../../graphql-types';
import { Result } from '../types';

const Link = styled(NavLink)`
  text-decoration: none;
  white-space: nowrap;
`;

const MenuSearch = styled(MenuItem)`
  padding: 0 1.2em 0 0 !important;
  height: 3.5rem !important;
`;

const HeaderComponent = styled(Menu)`
  flex-flow: row nowrap;
  height: auto !important;

  & ${MenuSearch} {
    height: 3.5rem !important;
  }

  & > a:first-child > ${MenuItem} {
    flex: 0 0 180px;
  }

  img {
    padding-right: 0.5em;
  }

  & ${MenuItem}:not(${MenuSearch}) {
    &:hover,
    &:focus,
    &:active,
    &:hover:focus {
      background-color: ${({ theme }) => theme.greyscale.light};
      color: ${({ theme }) => theme.greyscale.darkest};
    }
  }

  & ${MenuGroup} {
    justify-content: center;
    flex-wrap: wrap;
    flex: 1 1 100%;
    margin-left: 108px; /* Offset from version dropdown */

    & ${MenuItem} {
      color: ${({ theme }) => theme.colors.primary.default};
    }
  }

  ${Utils.below(1300)} {
    flex-wrap: wrap;

    & ${MenuGroup} {
      flex: 0 0 100%;
      margin-left: 0;
      order: 2;
    }

    & > ${MenuSearch}, ${MenuGroup} {
      padding: 0;
    }
  }

  ${Utils.below('sm')} {
    & ${MenuSearch} {
      flex: 0 0 100%;
      padding: ${({ theme }) => theme.sizes.padding.normal};

      & > div,
      & input {
        width: 100%;
      }
    }
  }
`;

const VersionDropdown = styled(Dropdown)`
  margin-right: 8px;
  min-width: 100px !important;
  width: 100px !important;
`;

const VersionLink = styled.a`
  display: block;
  padding-left: 1em;
  cursor: pointer;
  text-decoration: none;
`;

const ExternalIcon = styled.span`
  position: absolute;
  top: 0.75em;
  right: 0;

  font-family: 'FontAwesome';
  font-size: 0.75em;
  &:before {
    content: '\f08e';
  }
`;

export interface HeaderProps {
  siteTitle?: string;
  onSearch?: (res: Result) => void;
}

export const Header: React.FC<HeaderProps> = ({ siteTitle = '', onSearch }) => {
  const [open, setOpen] = useState(false);
  const outRef = Utils.useOnClickOutside<HTMLDivElement>(() => setOpen(false));

  const index = useRef(null);
  const [search, setSearch] = useState('');
  const location = useLocation();

  const {
    allNavigationYaml: { edges },
    allVersionsYaml: { edges: supportedVersions },
    siteSearchIndex,
  } = useStaticQuery<GetNavItemsQuery>(graphql`
    query GetNavItems {
      allNavigationYaml {
        edges {
          node {
            link
            name
            external
            id
          }
        }
      }

      allVersionsYaml {
        edges {
          node {
            link
            name
          }
        }
      }

      siteSearchIndex {
        index
      }
    }
  `);

  const versionOptions = useMemo(
    () =>
      supportedVersions.map((e) => ({
        value: e.node.link,
        label: e.node.name,
      })),
    [supportedVersions],
  );

  const versionSelection = useMemo(() => {
    const prefix = location.pathname.split('/').filter(Boolean)[0];

    if (!prefix) {
      return versionOptions[0];
    }

    return (
      versionOptions.find((o) => o.value === `/${prefix}`) || versionOptions[0]
    );
  }, [location.pathname, versionOptions]);

  useEffect(() => {
    if (search.length === 0) {
      onSearch?.(null);

      return;
    }

    if (!index.current) {
      index.current = Index.load(siteSearchIndex.index);
    }

    const res = index.current
      .search(search, {})
      .map(({ ref }) => index.current.documentStore.getDoc(ref));

    onSearch?.(res);
  }, [siteSearchIndex, search, onSearch]);

  return (
    <HeaderComponent>
      <Link to="/">
        <MenuItem>
          <img
            src={withPrefix('/icons/icon-72x72.png')}
            width={24}
            height={24}
            alt="HIRO Logo"
          />
          {siteTitle}
        </MenuItem>
      </Link>
      {edges.length > 1 && (
        <MenuGroup>
          {edges.map(({ node }) => (
            <Link
              key={node.id}
              to={node.link}
              target={node.external ? '_blank' : undefined}
              rel={node.external ? 'noopener noreferrer' : undefined}
            >
              <MenuItem>
                {node.external && <ExternalIcon />}
                {node.name}
              </MenuItem>
            </Link>
          ))}
        </MenuGroup>
      )}
      <MenuSearch position="right">
        {!!versionOptions?.length && (
          <VersionDropdown
            compact
            text={versionSelection.label}
            open={open}
            onClick={() => setOpen((o) => !o)}
            outRef={outRef}
          >
            <Dropdown.ListWrapper>
              {versionOptions.map((o) => (
                <VersionLink href={o.value} key={o.value}>
                  {o.label}
                </VersionLink>
              ))}
            </Dropdown.ListWrapper>
          </VersionDropdown>
        )}
        <Search
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClear={() => setSearch('')}
        />
      </MenuSearch>
    </HeaderComponent>
  );
};
